import {ReactNode} from "react";
import {Box, Typography} from "@mui/material";
import {
  BarChart,
  Gauge,
  LineChart,
  PieChart,
  RadarChart,
  ScatterChart,
  SparkLineChart,
} from "@mui/x-charts";
import {useThemeContext} from "@theme";
import dayjs from "dayjs";
import {format} from "@constants";
import {ChatChartSpec} from "./chatChartParse";

const CHART_HEIGHT = 250;
const SPARKLINE_HEIGHT = 120;
const GAUGE_HEIGHT = 200;

const defaultColors = (theme: ReturnType<typeof useThemeContext>["theme"]) => [
  theme.colors.tertiary,
  theme.colors.blue,
  theme.colors.green,
  theme.colors.orange,
  theme.colors.purple,
  theme.colors.teal,
  theme.colors.cyan,
  theme.colors.red,
];

const ChartTitle = ({title}: {title?: string}) =>
  title ? (
    <Typography variant="caption" fontWeight={600} sx={{display: "block", mb: 0.5}}>
      {title}
    </Typography>
  ) : null;

const ChatChart = ({spec}: {spec: ChatChartSpec}) => {
  const {theme} = useThemeContext();
  const palette = defaultColors(theme);

  const shell = (chart: ReactNode) => (
    <Box sx={{width: "100%", minWidth: 0}}>
      <ChartTitle title={spec.title} />
      <Box sx={{width: "100%", minWidth: 0, "& svg": {maxWidth: "100%"}}}>
        {chart}
      </Box>
    </Box>
  );

  if (spec.type === "gauge") {
    return shell(
      <Gauge
        height={GAUGE_HEIGHT}
        value={spec.value}
        valueMin={spec.valueMin}
        valueMax={spec.valueMax}
        startAngle={-110}
        endAngle={110}
        sx={{width: "100%"}}
        skipAnimation
      />,
    );
  }

  if (spec.type === "sparkline") {
    return shell(
      <SparkLineChart
        height={SPARKLINE_HEIGHT}
        plotType={spec.plotType}
        data={spec.data}
        margin={{top: 8, bottom: 8, left: 4, right: 4}}
        sx={{width: "100%"}}
      />,
    );
  }

  if (spec.type === "pie") {
    const data = spec.series.map((slice, index) => ({
      id: slice.label,
      label: slice.label,
      value: slice.value,
      color: slice.color ?? palette[index % palette.length],
    }));
    return shell(
      <PieChart
        height={CHART_HEIGHT}
        hideLegend={spec.series.length <= 6}
        series={[{data, innerRadius: 24, paddingAngle: 1, cornerRadius: 4}]}
        sx={{width: "100%"}}
        skipAnimation
      />,
    );
  }

  if (spec.type === "scatter") {
    const series = spec.series.map((item, index) => ({
      id: item.id ?? item.label,
      label: item.label,
      data: item.points.map((point, pointIndex) => ({
        x: point.x,
        y: point.y,
        id: `${item.label}-${pointIndex}`,
      })),
      color: item.color ?? palette[index % palette.length],
    }));
    return shell(
      <ScatterChart
        height={CHART_HEIGHT}
        xAxis={[{label: spec.xLabel}]}
        yAxis={[{width: 48, label: spec.yLabel}]}
        series={series}
        grid={{vertical: true, horizontal: true}}
        margin={{top: 12, right: 12, left: 4, bottom: 20}}
        hideLegend
        sx={{width: "100%"}}
      />,
    );
  }

  if (spec.type === "radar") {
    const series = spec.series.map((item, index) => ({
      id: item.id ?? item.label,
      label: item.label,
      data: item.data,
      color: item.color ?? palette[index % palette.length],
    }));
    return shell(
      <RadarChart
        height={CHART_HEIGHT}
        series={series}
        radar={{metrics: spec.categories}}
        hideLegend={series.length <= 1}
        sx={{width: "100%"}}
      />,
    );
  }

  const baseSeries = spec.series.map((item, index) => ({
    id: item.id ?? item.label,
    label: item.label,
    data: item.data,
    color: item.color ?? palette[index % palette.length],
  }));

  const xAxis =
    spec.mode === "time"
      ? [
          {
            scaleType: "time" as const,
            data: spec.timeAxis,
            label: spec.xLabel,
            valueFormatter: (value: Date | number, context: {location: string}) => {
              const date = value instanceof Date ? value : new Date(value);
              if (Number.isNaN(date.getTime())) {
                return "";
              }
              if (context.location === "tick") {
                return dayjs(date).format(format.time.short);
              }
              return dayjs(date).format(format.datetime.long);
            },
          },
        ]
      : [
          {
            scaleType: "point" as const,
            data: spec.categoriesAxis,
            label: spec.xLabel,
          },
        ];

  const layout = {
    height: CHART_HEIGHT,
    xAxis,
    yAxis: [{width: 48, label: spec.yLabel}],
    margin: {top: 12, right: 12, left: 4, bottom: 20},
    grid: {vertical: true, horizontal: true} as const,
    hideLegend: true,
    sx: {width: "100%"},
  };

  return shell(
    spec.type === "bar" ? (
      <BarChart
        {...layout}
        series={baseSeries.map(item => ({...item, type: "bar" as const}))}
      />
    ) : (
      <LineChart
        {...layout}
        series={baseSeries.map(item => ({
          ...item,
          type: "line" as const,
          showMark: false,
          curve: "linear" as const,
        }))}
        skipAnimation
      />
    ),
  );
};

export {ChatChart};
